import { Box, Card, CardHeader, CircularProgress, darken, Grid, lighten, Skeleton, Stack, TableBody, TableCell, TableHead, TableRow, Typography, emphasize, useTheme } from "@mui/material";
import { addDays, addYears, eachDayOfInterval, formatDuration, intervalToDuration } from "date-fns";
import { ReduxState, registerResource, ShowBase, useGetMainList, useGetOne, useRecordContext, useShowContext } from "ra-core";
import Chart from 'react-apexcharts';
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import numeral from "numeral";
import { format as formatDfn } from "date-fns";
import { Content } from "~/components/content/Content";
import { PopTimeRangeKind, usePopTimeRangePicker } from "~/components/input/PopTimeRangePicker";
import { AutoEncryptReportSummaryStatisDto, AutoEncryptReportSummaryTableQueryResponseDto as TableRespDto } from "~/gen/api/securefile";
import { OdResourcePageProps } from "~/types/resource";
import { useResourceRegister } from "~/utils/resourceRegister";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import { PrimaryClassification, SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { RecPatch } from "~/dataProvider/tools";
import { SimpleOption } from "~/components/governance/search/AmbientFacilitiesContext";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { DatagridRoot } from "~/components/list/datagrid/useDatagridStyles";
import Scrollbar from "~/components/Scrollbar";
import { crudGetManyEx } from "~/actions/crudFetchEx";
import { ClientNameDto } from "~/gen/api/user";
import { useSnackbar } from "notistack";
import retrieveAxiosErr from "~/utils/retrieveAxiosErr";
import { BackToTopButton } from "../layout/Layout";



const statisChartsRes = 'securefile-dm-reports-statis-charts'
const statisTableRes = 'securefile-dm-reports-statis-table'
const dmClientNameRes = 'securefile-dm-client-names'

const DataManagerReports: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: statisChartsRes })
  useResourceRegister({ name: statisTableRes })
  useResourceRegister({ name: dmClientNameRes })

  const registered = useSelector<ReduxState>(store => {
    return !!store.admin.resources[statisChartsRes]
  })

  if (!registered) { return null }

  return (
    <DataManagerReportsBody {...props} />
  )
}

const defaultPageSize = 100

const DataManagerReportsBody: FC<OdResourcePageProps> = ({
  options, resource,
}) => {
  const [states, setStates] = useState(() => {
    const initialS = {
      notBefore: (addDays(new Date(), -7)).toISOString(),
      notAfter: new Date().toISOString(),
      table: {
        anchor: null as string|null,
        page: 1,
        loadedPageIds: [] as string[],
        cachedNodes: [] as any[],
        clientNameMap: {} as Record<string, string>,
        nextPageId: '',
        loadedCount: 0,
      },
      totalReportCount: null as number|null,
    }
    initialS.table.nextPageId = JSON.stringify({ 
      notBefore: initialS.notBefore, 
      notAfter: initialS.notAfter, 
      anchor: initialS.table.anchor,
      page: initialS.table.page,
      limit: defaultPageSize,
    })
    return initialS
  })

  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const format = useUserTimeFormat()
  const tableFragmentData : Record<string, TableRespDto> = 
    useSelector<ReduxState, any>(store => store.admin.resources[statisTableRes].data ?? {})

  const {updateNextPageInfo, setSearchTime, setTotalReportCount} = useMemo(() => ({
    updateNextPageInfo: (info: {pageId:string; anchor:string; count:number;}) => {
      setStates(v => {
        const p = JSON.parse(info.pageId).page
        if (v.table.loadedPageIds.some(x => JSON.parse(x).page === p)) { return v }
        const v1 = ({...v,
          table: {...v.table,
            anchor: info.anchor,
            page: v.table.loadedPageIds.length + 2,
            loadedPageIds: [...v.table.loadedPageIds, info.pageId],
            loadedCount: v.table.loadedCount + info.count
          }
        })
        return v1
      })    
    },
    setSearchTime: (start: Date|null, end: Date|null) => {
      setStates(v => {
        const v1 = {...v,
          notBefore: (start ?? addDays(new Date(), -7)).toISOString() as any,
          notAfter: (end ?? new Date()).toISOString()  as any,
          table: {
            anchor: null,
            page: 1,
            loadedPageIds: [],
            cachedNodes: [],
            clientNameMap: v.table.clientNameMap,
            nextPageId: '',
            loadedCount: 0,
          },
          totalReportCount: null,
        }
        v1.table.nextPageId = JSON.stringify({
          notBefore: v1.notBefore, 
          notAfter: v1.notAfter, 
          anchor: v1.table.anchor,
          page: v1.table.page,
          limit: defaultPageSize,
        })
        return v1
      })
      // dispatch(registerResource({name:statisTableRes}))
    },
    setTotalReportCount: (x:number) => {
      setStates(v => ({...v, totalReportCount: x }))
    }
  }), [])

  const scrollHostEl = useRef<HTMLDivElement>()
  const scrollListener = useCallback(() => {
    if (scrollHostEl.current) {
      const el = scrollHostEl.current
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 5) {
        if (Object.keys(tableFragmentData).length === 0) { return }
        setStates(v => {
          if (v.totalReportCount == null) { return v }
          if (v.totalReportCount <= v.table.loadedCount) { return v}
          const nextPageId = JSON.stringify({ 
            notBefore: v.notBefore, 
            notAfter: v.notAfter, 
            anchor: v.table.anchor,
            page: v.table.page,
            limit: defaultPageSize,
          })
          const tableDto = tableFragmentData[v.table.nextPageId]
          if (v.table.nextPageId !== nextPageId && !!tableDto) {
            const n = v.table.cachedNodes.length
            const v1 = {...v, 
              table: {...v.table, 
                nextPageId,
                cachedNodes: [...v.table.cachedNodes,
                  <ReportRowsFragment key={n} format={format} clientNameMap={states.table.clientNameMap} 
                    tableDto={tableDto} 
                  />
                ]
              }
            }
            return v1
          }
          return v
        })
      }
    }
  }, [format, states.table.clientNameMap, tableFragmentData])
  
  useLayoutEffect(() => {
    scrollHostEl.current = window.document.querySelector('div.scroll-host') as any
    const el = scrollHostEl.current!
    el.addEventListener('scroll', scrollListener)
    return () => {
      el.removeEventListener('scroll', scrollListener)
    }
  }, [scrollListener])

  const chartStatisOnFailure = (err: any) => {
    const errMsg = retrieveAxiosErr(err)
    enqueueSnackbar(`Error occurred, please refresh the page later: ${errMsg}`, { variant: 'error' })
  }
  
  return (
    <Content pageName="Data Manager Scan Reports" resourceName="Data Manager Scan Reports" breadcrumbs={options?.breadcrumbs}>
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <ShowBase resource={statisChartsRes} basePath="" id={`${states.notBefore}|${states.notAfter}`} onFailure={chartStatisOnFailure}>
          <StatisChart setSearchTime={setSearchTime} setTotalReportCount={setTotalReportCount} />
        </ShowBase>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Reports" />
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <DatagridRoot>
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Protected / Classified</TableCell>
                      <TableCell>Scan Job Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {states.table.cachedNodes}
                    <ReportRowsFragmentLoader 
                      nextPageId={states.table.nextPageId} 
                      clientNameMap={states.table.clientNameMap} 
                      updateNextPageInfo={updateNextPageInfo} 
                      format={format} 
                    />
                  </TableBody>
                </DatagridRoot>
              </Box>
            </Scrollbar>
            {/* {states.totalReportCount != null &&
              <Box textAlign="center">
                <Typography variant="caption">Loaded {states.table.loadedCount} of {states.totalReportCount}</Typography>
              </Box>} */}
          </Card>
          <BackToTopButton />
        </Grid>
      </Grid>
    </Content>
  )
}

export default DataManagerReports;


const ReportRowsFragment : FC<{tableDto:TableRespDto, clientNameMap: Record<string, string>, format:(d:any,s:string)=>string}> = ({
  tableDto, clientNameMap, format,
}) => {
  
  return (
    <>
      {tableDto.items!.map((x, i) => {
        const duration = x.startTime 
          ? formatDuration(intervalToDuration({ start: new Date(x.startTime), end: new Date(x.endTime!) }))
          : '-'
        
        return (
          <TableRow key={i}>
            <TableCell>
              <Typography variant="body2">{format(x.endTime!, "Pp")}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{clientNameMap[x.clientId!]}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                <Typography color="primary" component="span">{commFormatter(x.entryCount ?? 0)}</Typography> {" / "}
                <Typography color="success" component="span">{commFormatter(Object.values(x.classificationStatisBag?.primaryClassification ?? {}).reduce((a, b) => a + b, 0))}</Typography>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{duration}</Typography>
            </TableCell>
          </TableRow>
        )
      })}
    </>
  )
}

const ReportRowsFragmentLoader : FC<{ 
  nextPageId:string, 
  clientNameMap: Record<string, string>,
  format:(d:any,s:string)=>string,
  updateNextPageInfo: (info: {pageId:string; anchor:string; count:number;}) => void
}> = ({
  nextPageId, clientNameMap, format, updateNextPageInfo,
}) => {
  const [states, setStates] = useState({
    loadingClientNames: false,
  })
  const dispatch = useDispatch()
  const { data, loading, loaded } = useGetOne<TableRespDto&RecPatch>(statisTableRes, nextPageId, {
    onSuccess: ({data}) => {
      const dto = data as TableRespDto
      updateNextPageInfo({pageId: nextPageId, anchor: dto.endAnchor!, count: dto.items?.length ?? 0 });
      let clientIds = new Set((dto.items ?? []).map(x => x.clientId!))
      clientIds = clientIds.difference(new Set(Object.keys(clientNameMap)))
      if (clientIds.size > 0) {
        dispatch(crudGetManyEx(dmClientNameRes, [...clientIds], {
          onSuccess: ({data}) => {
            const clientNames : ClientNameDto[] = data
            for (const cname of clientNames) {
              clientNameMap[cname.clientId!] = cname.clientName!
            }
            setStates(v => ({...v, loadingClientNames: false}))
          }
        }))
      } else {
        setStates(v => ({...v, loadingClientNames: false}))
      }
    },
  })
  useEffect(() => {
    setStates(v => ({...v, loadingClientNames: true}))
  }, [nextPageId])

  return (
    <>
      {(loading || states.loadingClientNames) &&
        <TableRow>
          <TableCell colSpan={4}>
            <Box textAlign="center">
              <CircularProgress size={28} />
            </Box>
          </TableCell>
        </TableRow>}
      {loaded && !states.loadingClientNames && data &&
        <ReportRowsFragment format={format} tableDto={data} clientNameMap={clientNameMap} />}
    </>
  )
}

const StatisChart : FC<{setSearchTime: (start: Date|null, end: Date|null)=>void, setTotalReportCount: (x:number)=>void}> = ({
  setSearchTime, setTotalReportCount,
}) => {
  const [states, setStates] = useState({
    classificationSelection: PrimaryClassification.Confidential,
    sndClssMap: {} as Record<string, {text:string[],count:number}[]>,
    timerangeCustom: { startBoundary: addYears(new Date(), -1) }
  })
  const format = useUserTimeFormat()
  const theme = useTheme()
  const { record: statisDto, loading, loaded } = useShowContext<AutoEncryptReportSummaryStatisDto&RecPatch>()
  const { PopTimeRangePicker, open, states: tStates } = usePopTimeRangePicker({
    label: '',
    defaultKind: PopTimeRangeKind.Days_7,
    onChange: ({kind, start, end}) => {
      setSearchTime(start, end)
    },
    PopoverProps: {
      anchorOrigin: { vertical: 'top', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
    },
    custom: states.timerangeCustom,
    // excludesOptions: [PopTimeRangeKind.Hours_24]
  })
  useEffect(() => {
    if (loaded && statisDto) {
      setTotalReportCount(statisDto.grandTotal?.summariesCount ?? 0)
    }
  }, [loaded, setTotalReportCount, statisDto])

  const { data: data2, ids: ids2 } = useGetMainList<SecondaryClassificationResponseDto&RecPatch>('settings/governance/data-classification', pages(), sorts(), {})

  const {classifications} = useMemo(() => ({
    classifications: (ids2 ?? [])
      .map((id) => {
        const c = data2![id as number]
        const s : SimpleOption = c as any
        s.key = c.secondaryClassificationId!
        s.text = `${c.primaryClassificationText} - ${c.name}`
        return s as any
      })
      .sort((a, b) => 
            ((a.primaryClassification || 0) - (b.primaryClassification || 0)) 
        || ((a.secondaryClassificationId || 0) - (b.secondaryClassificationId || 0))) as SecondaryClassificationResponseDto[],
  }), [data2, ids2])

  const {chartOptions: docTypeChartOptions, chartHeight} = useMemo(() => {
    const [categories, data] : [string[], number[]] =
      Object.entries(statisDto?.documentTypeDistribution ?? {}).sort((a, b) => a[1] === b[1] ? (a[0] < b[0] ? -1 : 1) : a[1] - b[1] < 0 ? 1 : -1)
        .reduce((acc, x) => {
          if (acc[0].length > 15) { return acc } // at most 16 data points
          const maxCount = acc[1][0] ?? 0
          if (x[1] < (maxCount / 40)) { return acc } // proportion too small to render
          acc[0].push(x[0])
          acc[1].push(x[1])
          return acc
        }, [[], []] as any)
    const chartOptions = {
      series: [{ name: 'file count', data }],
      options: {
        chart: { toolbar: { show: false }, },
        plotOptions: {
          bar: {
            borderRadius: 4,
            borderRadiusApplication: 'end',
            horizontal: true,
          }
        },
        dataLabels: { enabled: true, formatter: commFormatter },
        xaxis: { categories, 
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { show: false },
        },
        yaxis: { labels: { minWidth: 80 } },
        grid: { show: false },
        tooltip: {
          y: { formatter: commFormatter }
        },
        theme: { mode: theme.palette.mode }
      },
    };
    const chartHeight = Math.floor(Math.max(data.length * (275 / 8), 275))
    return {chartOptions, chartHeight}
  }, [statisDto?.documentTypeDistribution, theme.palette.mode])

  const clssPieChartOptions = useMemo(() => {
    const [labels, series] : [string[], number[]] = Object.entries(statisDto?.primaryClssDistribution ?? {}).sort((a, b) => a[1] - b[1] < 0 ? 1 : -1)
      .reduce((acc, x) => {
        acc[0].push(x[0])
        acc[1].push(x[1])
        return acc
      }, [[], []] as any)
    return ({
      series,
      options: {
        chart: { width: 180, 
          events: {
            dataPointSelection: (e: any, c: any, selection: any) => {
              setStates(v => {
                const selectedClss : any = PrimaryClassification[labels[selection.dataPointIndex as number] as any]
                return v.classificationSelection === selectedClss ? v : {...v, classificationSelection: selectedClss }
              })
            },
          },
        },
        labels,
        tooltip: { y: { formatter: commFormatter, show: true } },
        theme: { mode: theme.palette.mode }
      },
    })
  }, [statisDto?.primaryClssDistribution, theme.palette.mode])

  useEffect(() => {
    const sndClssMap = Object.entries(statisDto?.secondaryClssDistribution ?? {}).reduce((acc, x) => {
      const cid = +x[0]
      const clss = classifications.find(c => c.secondaryClassificationId === cid)
      if (clss) {
        const arr = acc[clss.primaryClassification!] ?? (acc[clss.primaryClassification!] = [])
        arr.push({
          text: [PrimaryClassification[clss.primaryClassification!], clss.name!],
          count: x[1]
        })
      }
      return acc
    }, {} as Record<string, {text:string[],count:number}[]>)
    for (const arr of Object.values(sndClssMap)) {
      arr.sort((a, b) => a.count - b.count < 0 ? 1 : -1)
    }
    const keys = Object.keys(sndClssMap).sort()
    setStates(v => ({...v, sndClssMap,
      ...(keys.length === 0 || sndClssMap[v.classificationSelection] ? {} 
        : {classificationSelection: +keys[0]})
    }))
  }, [classifications, statisDto?.secondaryClssDistribution])
  
  const sndClssChartOptions = useMemo(() => {
    const arr = states.sndClssMap[states.classificationSelection as any]
    if (!arr) { return null }
    const categories = arr.map(x => x.text)
    const data = arr.map(x => x.count)
    const sndClssChartOptions = {
      series: [{ name: 'file count', data, }],
      options: {
        chart: { height: 200, toolbar: { show: false }, },
        plotOptions: {
          bar: {
            columnWidth: '45%',
            distributed: true,
            dataLabels: { position: 'top' },
          }
        },
        dataLabels: { enabled: true,
          offsetY: -20,
          style: { fontSize: '12px', colors: [theme.palette.text.secondary] }, 
          formatter: commFormatter,
        },
        legend: { show: false },
        xaxis: { categories, },
        yaxis: { labels: { formatter: (val: number) => val < 1 && val !== 0 ? '' : commFormatter(val) } },
        theme: { mode: theme.palette.mode }
      },
    }
    return sndClssChartOptions
  }, [states.classificationSelection, states.sndClssMap, theme.palette.mode, theme.palette.text.secondary])

  const filesByDayChartOptions = useMemo(() => {
    if (!statisDto) { return null }
    const tStart = new Date(statisDto.startEnd?.item1!)
    const tEnd = new Date(statisDto.startEnd?.item2!)
    const dates = eachDayOfInterval({ start: tStart, end: tEnd }).map(d => d.toDateString() + ' GMT')
    let displayedDates = dates
    let dataPoints = statisDto.fileCountsByDay ?? []
    const showByWeek = dates.length > 32
    if (showByWeek) {
      const iSunday = dates.findIndex(dStr => {
        const d = new Date(dStr)
        return d.getDay() === 1 // find the first Sunday
      })
      const fileCountsByDay = statisDto.fileCountsByDay ?? []
      const {pointLabels, countsByWeek} = dates.reduce((acc, d, i) => {
        if (i === acc.ws) {
          acc.pointLabels.push(d)
          acc.countsByWeek.push(fileCountsByDay[i])
        } else {
          const c = acc.countsByWeek.length - 1
          acc.countsByWeek[c] = acc.countsByWeek[c] + fileCountsByDay[i]
        }
        if (i === acc.we) {
          acc.ws = acc.we + 1
          acc.we = acc.we + 7
        }
        return acc
      }, { ws: 0, we: iSunday !== 0 ? iSunday - 1 : 6, pointLabels: [] as string[], countsByWeek: [] as number[]})
      displayedDates = pointLabels
      dataPoints = countsByWeek
    }

    var options = {
      series: [{ name: 'file count', data: dataPoints }],
      options: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false
          },
        },
        xaxis: {
          type: 'datetime',
          categories: displayedDates,
          labels: { datetimeUtc: false }
        },
        yaxis: { labels: { formatter: (val: number) => val < 1 && val !== 0 ? '' : commFormatter(val) } },
        tooltip: {
          ...(showByWeek && {x: { formatter: (val: number, opts?: any) => 'the week of ' + formatDfn(val, 'dd MMM') }}),
          y: { formatter: commFormatter }
        },
        theme: { mode: theme.palette.mode },
        dataLabels: {
          ...(displayedDates.length > 15 ? { enabled: false } : { formatter: commFormatter, }),
          style: { colors: [theme.palette.mode === 'light' ? darken(theme.palette.info.main, 0.5) : theme.palette.common.white] }
        },
      },
    }
    return {options, showByWeek}
  }, [statisDto, theme.palette.common.white, theme.palette.info.main, theme.palette.mode])
  
  if (!statisDto) {
    return (
      <Grid item xs={12} container sx={{ '& .MuiSkeleton-root': { mr: 2, mb: 2 } }}>
        <Grid item xs={9}>
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={180} />
        </Grid>
        <Grid item xs={3}>
          <Skeleton variant="rectangular" height={316} />
        </Grid>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={180} />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid item xs={12} container
      sx={{
        '& .chart-segment': {
          bgcolor: theme => theme.palette.background.paper,
          boxShadow: theme => `0 0 8px ${darken(theme.palette.background.default, 0.15)}`,
          '& svg': { bgcolor: 'unset !important' },
        },
      }}
    >
      <Grid item xs={9}>
        <Box sx={{ mr: 2 }}>
          <Box padding={2} className="chart-segment" marginBottom={2}>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: '180px repeat(4, 1fr)',
              '& div': { textAlign: 'center', },
              '& h4': { textAlign: 'center', fontWeight: 300, color: 'success.light' },
              '& div.label-box': { minHeight: 40, bgcolor: theme => lighten(theme.palette.primary.main, 0.90), ml: -2, mr: 2 },
              '& div.header-box': { 
                borderBottom: theme => `1px solid ${theme.palette.divider}`, 
                color: theme => emphasize(theme.palette.background.default, 0.52),
                mt: -1.5,
              },
            }}>
              <Stack direction="row" className="label-box" justifyContent="center" alignItems="center"
                sx={{ gridArea: '1 / 1 / span 2', my: -2 }}
              >
                {
                    tStates.kind === PopTimeRangeKind.Hours_24 ? 'Last 24 hours'
                  : tStates.kind === PopTimeRangeKind.Days_7 ? 'Last 7 days'
                  : tStates.kind === PopTimeRangeKind.Days_30 ? 'Last 30 days'
                  : tStates.kind === PopTimeRangeKind.Months_3 ? 'Last 3 months'
                  : tStates.kind === PopTimeRangeKind.Months_6 ? 'Last 6 months'
                  : tStates.kind === PopTimeRangeKind.Years_1 ? 'Last 1 year'
                  : tStates.kind === PopTimeRangeKind.Custom ? 
                    <Box sx={{ transform: 'scale(0.8)', }}>{
                      !tStates.end ? <Typography variant="caption">From {format(tStates.start!, 'Pp')}</Typography>
                    : !tStates.start ? <Typography variant="caption">To {format(tStates.end!, 'Pp')}</Typography>
                    : <>
                      <Typography variant="caption" component="p">From {format(tStates.start, 'Pp')}</Typography>
                      <Typography variant="caption">To {format(tStates.end, 'Pp')}</Typography>
                      </>
                    }</Box>
                  : ''
                }
                <Box sx={{ flex: '0 0 auto', '& button': { minWidth: 'unset', p: 'unset', pr: 1 } }}>
                  {PopTimeRangePicker}
                </Box>
              </Stack>
              <Box className="header-box"><Typography variant="caption">Reports</Typography></Box>
              <Box className="header-box"><Typography variant="caption">Protected Files</Typography></Box>
              <Box className="header-box"><Typography variant="caption">Classified Files</Typography></Box>
              <Box className="header-box"><Typography variant="caption">Active Clients</Typography></Box>
              
              <Typography variant="h4">
                {commFormatter(statisDto.grandTotal?.summariesCount ?? 0)}
              </Typography>
              <Typography variant="h4">
                {commFormatter(statisDto.grandTotal?.totalFilesCount ?? 0)}
              </Typography>
              <Typography variant="h4">
                {commFormatter(statisDto.grandTotal?.classifiedCount ?? 0)}
              </Typography>
              <Typography variant="h4">
                {commFormatter(statisDto.activeClientsCount ?? null)}
              </Typography>
            </Box>
          </Box>
          <Stack className="chart-segment" padding={2} direction="row">
            {clssPieChartOptions.series.length === 0
              ? <Box height={232} width="100%">
                  <Typography variant="body1" marginBottom={1} color="text.primary">Classification</Typography>
                  <Typography variant="body2" color="warning.main" textAlign="center" paddingTop={8}>No Data</Typography>
                </Box>
              : <>
                  <Box width={300} sx={{ '& > div': { ml: -1 } }}>
                    <Typography variant="body1" marginBottom={1} color="text.primary">Classification</Typography>
                    <Chart height="200" type="pie" {...(clssPieChartOptions as any)} />
                  </Box>
                  {sndClssChartOptions &&
                    <Box sx={{ flex: '1 1 auto'}}>
                      <Chart height="200" type="bar" {...(sndClssChartOptions as any)} />
                    </Box>}
                </>}
          </Stack>
        </Box>
      </Grid>
      <Grid item xs={3}>
        <Box className="chart-segment" padding={2} paddingBottom={0} height={331}>
          <Box padding={2} marginX={-2} marginTop={-2}>
            <Typography variant="body1" color="text.primary">Document Types</Typography>
          </Box>
          {docTypeChartOptions.series[0].data.length === 0
            ? <Box height={275}>
                <Typography variant="body2" color="warning.main" textAlign="center" paddingTop={8}>No Data</Typography>
              </Box>
            : 
            <Box height={291}>
              <Scrollbar>
                <Chart height={chartHeight} type="bar" {...(docTypeChartOptions as any)} />
              </Scrollbar>
            </Box>
          }
        </Box>
      </Grid>
      <Grid item xs={12} marginTop={2}>
        <Box className="chart-segment" padding={2}>
          <Typography variant="body1" color="text.primary">Protected Files by {filesByDayChartOptions?.showByWeek ? 'week' : 'day'}</Typography>
          {(statisDto.grandTotal?.summariesCount ?? 0) === 0 
            ? <Box height={175} width="100%">
                <Typography variant="body2" color="warning.main" textAlign="center" paddingTop={8}>No Data</Typography>
              </Box>
            : <Chart height="160" type="bar" {...(filesByDayChartOptions?.options as any)} />}
        </Box>
      </Grid>
    </Grid>
  )
}

const commFormatter = (val:number|null) => numeral(val).format('0,0')
